// The device manager's data hook: lists the Owner's cameras via `GET /api/devices`
// and revokes one via `POST /api/devices/:id/revoke`, reflecting the new status in
// place. Thin over the `api` client (which does the request shaping + error
// normalization); this only sequences the calls + holds React state, so it is
// unit-testable with a stub client (like `useMedia` / `useClaim`).
//
// Revoke updates the local row to `revoked` from the Worker's response (the source
// of truth) rather than re-listing — instant, and the Worker already returns the
// new status. Per-device revoke-in-flight is tracked so only the affected row's
// button disables.

import { useCallback, useEffect, useRef, useState } from "react";
import { type ApiClient, type Device, ApiError } from "./api";

export interface UseDevicesState {
  devices: Device[];
  /** True during the first list load (drives the initial spinner). */
  loading: boolean;
  /** A non-auth error message to surface (auth → `unauthorized`). */
  error: string | null;
  /** The Owner Session is missing/expired — show the re-auth affordance. */
  unauthorized: boolean;
  /** The deviceId currently being revoked, or null (disables just that row). */
  revoking: string | null;
  /** Revoke an owned camera; on success its row flips to `revoked` in place. */
  revoke: (deviceId: string) => Promise<void>;
  /** Reload the list from scratch. */
  reload: () => void;
}

/** Manage the Owner's camera list + the revoke action for the device manager. */
export function useDevices(client: ApiClient): UseDevicesState {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  // Invalidate an in-flight list when a reload supersedes it (stale-write guard).
  const reqId = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const list = await client.getDevices();
      if (id !== reqId.current) return;
      setDevices(list);
    } catch (err) {
      if (id !== reqId.current) return;
      if (err instanceof ApiError && err.isUnauthorized) {
        setUnauthorized(true);
      } else {
        setError(err instanceof Error ? err.message : "failed to load devices");
      }
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [client]);

  const revoke = useCallback(
    async (deviceId: string): Promise<void> => {
      if (revoking) return;
      setRevoking(deviceId);
      setError(null);
      try {
        const result = await client.revokeDevice(deviceId);
        // Reflect the Worker-confirmed status on just this row (source of truth).
        setDevices((prev) =>
          prev.map((d) =>
            d.deviceId === deviceId ? { ...d, status: result.status } : d,
          ),
        );
      } catch (err) {
        if (err instanceof ApiError && err.isUnauthorized) {
          setUnauthorized(true);
        } else {
          setError(err instanceof Error ? err.message : "failed to revoke");
        }
      } finally {
        setRevoking(null);
      }
    },
    [client, revoking],
  );

  const reload = useCallback(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  return {
    devices,
    loading,
    error,
    unauthorized,
    revoking,
    revoke,
    reload,
  };
}
